import { useCallback, useRef, useEffect } from 'react';

/**
 * Creates a debounced version of a callback that delays execution until
 * a specified time has passed since the last call.
 *
 * Useful for batching multiple rapid updates (like SSE events) to reduce
 * the number of API calls or re-renders.
 *
 * @param callback - The function to debounce
 * @param delay - The delay in milliseconds (default: 300ms)
 * @returns A debounced version of the callback
 */
export function useDebouncedCallback<T extends (...args: never[]) => void>(
  callback: T,
  delay: number = 300
): T {
  const timeoutRef = useRef<number | null>(null);
  const callbackRef = useRef(callback);

  // Update the callback ref when it changes
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const debouncedCallback = useCallback(
    (...args: Parameters<T>) => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = window.setTimeout(() => {
        callbackRef.current(...args);
        timeoutRef.current = null;
      }, delay);
    },
    [delay]
  ) as T;

  return debouncedCallback;
}

/**
 * Creates a batched callback that collects multiple calls within a time window
 * and executes the callback once with combined results.
 *
 * Useful for batching SSE events that trigger multiple refetch operations.
 *
 * @param callback - The function to call after the batch window
 * @param delay - The batching window in milliseconds (default: 300ms)
 * @returns A function that starts the batch timer
 */
export function useBatchedCallback(
  callback: () => void,
  delay: number = 300
): () => void {
  const timeoutRef = useRef<number | null>(null);
  const callbackRef = useRef(callback);

  // Update the callback ref when it changes
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return useCallback(() => {
    // If there's already a pending batch, do nothing (first call wins)
    if (timeoutRef.current !== null) {
      return;
    }

    timeoutRef.current = window.setTimeout(() => {
      callbackRef.current();
      timeoutRef.current = null;
    }, delay);
  }, [delay]);
}
